import { usePrevious, useUpdateEffect } from 'react-use';

import { FormController, TextView } from '@dreryk/react-components';

import { medicalDevicesTranslationKeys } from '../../translations';
import {
   MedicalDeviceOrderFormRegister,
   MedicalDeviceOrderFormSetValue,
   MedicalDeviceOrderFormWatch,
   MedicalDeviceOrderItemsCountFormBeanPart,
   MedicalDeviceOrderPlacementFormBeanPart,
} from '../medicalDeviceOrder.types';

type ItemsCountFieldProps = {
   readonly register: MedicalDeviceOrderFormRegister<MedicalDeviceOrderItemsCountFormBeanPart>;
   readonly setValue: MedicalDeviceOrderFormSetValue<MedicalDeviceOrderItemsCountFormBeanPart>;
   readonly watch: MedicalDeviceOrderFormWatch<MedicalDeviceOrderPlacementFormBeanPart>;
};

const DEFAULT_ITEMS_COUNT = 1;

// eslint-disable-next-line @typescript-eslint/no-magic-numbers
type ItemsCountByPlacementMultiplier = 0 | 1 | 2;
const getItemsCountByPlacementMultiplier = (
   watch: MedicalDeviceOrderFormWatch<MedicalDeviceOrderPlacementFormBeanPart>
): ItemsCountByPlacementMultiplier => {
   const { placement, device } = watch(['device', 'placement']);
   if (!device?.fieldsConfiguration.placement.available) {
      return 1;
   }
   return placement?.length ?? 0;
};

export const ItemsCountReadOnlyField = (props: ItemsCountFieldProps): JSX.Element => {
   const device = props.watch('device');
   const placementFieldAvailable = device?.fieldsConfiguration.placement.available;
   const deviceUnitsLimit = device?.limit ?? DEFAULT_ITEMS_COUNT;
   const deviceItemsCount = deviceUnitsLimit * getItemsCountByPlacementMultiplier(props.watch);
   const previousDeviceItemsCount = usePrevious(deviceItemsCount);

   useUpdateEffect(() => {
      if (deviceItemsCount !== previousDeviceItemsCount) {
         props.setValue('itemsCount', deviceItemsCount);
      }
   }, [deviceItemsCount]);

   return (
      <>
         <TextView testId={'itemsCountReadOnly'} label={medicalDevicesTranslationKeys.fields.itemsCount}>
            {placementFieldAvailable ? deviceItemsCount : DEFAULT_ITEMS_COUNT}
         </TextView>
         <FormController controller={props.register('itemsCount')} defaultValue={DEFAULT_ITEMS_COUNT} />
      </>
   );
};
